import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { clientService, Client } from "../../services/client.service";
import { invoiceService } from "../../services/invoice.service";

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (invoiceId: number) => void;
}

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<number>(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      loadClients();
      // Reset form
      setSelectedClient(0);
      setInvoiceNumber("");
      setComment("");
      setError("");
    }
  }, [open]);

  const loadClients = async () => {
    try {
      const allClients = await clientService.getClients();
      // Filter to only active clients (statusID = 1)
      const activeClients = allClients.filter((c) => c.statusID === 1);
      setClients(activeClients);
    } catch (err) {
      setError("Failed to load clients");
    }
  };

  const handleCreate = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    if (!invoiceNumber.trim()) {
      setError("Please enter an invoice number");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const selectedClientData = clients.find((c) => c.id === selectedClient);

      const result = await invoiceService.createInvoice({
        invoiceNumber: invoiceNumber.trim(),
        clientID: selectedClient,
        clientName: selectedClientData?.companyName || "",
        comment: comment.trim(),
      });

      onSuccess(result.id);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create New Invoice</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {error && <Alert severity='error'>{error}</Alert>}

          <TextField
            select
            label='Client'
            value={selectedClient}
            onChange={(e) => setSelectedClient(Number(e.target.value))}
            fullWidth
            required
          >
            <MenuItem value={0}>Select a client...</MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.companyName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label='Invoice Number'
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label='Comment'
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant='contained'
          color='primary'
          disabled={loading || !selectedClient || !invoiceNumber.trim()}
        >
          {loading ? <CircularProgress size={24} /> : "Create Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceModal;
